import prismjs, { type Token } from "prismjs";
import "./prism-languages";
import { createRenderEffect, createSignal, For, type JSX, on, onCleanup } from "solid-js";
import { isServer } from "solid-js/web";
import type { CodeBlockASTNode } from "../toastmark/ast";

// Prism has no resumable lexer, so every pass re-scans the whole block. Past this size the block
// stays plain until it stops growing; a resumable highlighter would lift the cap.
const MAX_STREAMING_HIGHLIGHT_BYTES = 20_000;

// Nothing tells this component that the block stopped streaming — the AST node carries no
// completion flag — so a quiet gap stands in for it. Threading an isComplete prop down from the
// renderer options would make this exact.
const SETTLE_MS = 120;

const renderToken = (token: Token | string): JSX.Element => {
  if (typeof token === "string") {
    return <>{token}</>;
  }

  // Token object - apply CSS classes
  const className = token.type ? `token ${token.type}` : "token";

  // Handle different content types
  if (typeof token.content === "string") {
    return <span class={className}>{token.content}</span>;
  }

  if (Array.isArray(token.content)) {
    return <span class={className}>{token.content.map(renderToken)}</span>;
  }

  // Nested token
  return <span class={className}>{renderToken(token.content)}</span>;
};

export const HighlightedCodeBlock = (props: {
  node: Pick<CodeBlockASTNode, "literal" | "info">;
  class?: string;
  codeClass?: string;
}) => {
  const [highlighted, setHighlighted] = createSignal<{ tokens: (Token | string)[]; text: string }>({
    tokens: [],
    text: "",
  });

  const literal = () => props.node.literal || "";

  const tokenize = () => {
    const text = literal();
    if (highlighted().text === text) {
      return;
    }
    // An empty grammar is Prism's plaintext: the block comes back as one string token.
    setHighlighted({ tokens: prismjs.tokenize(text, prismjs.languages[props.node.info || ""] ?? {}), text });
  };

  let frame = 0;
  let settle: ReturnType<typeof setTimeout> | undefined;

  createRenderEffect(
    on(literal, (text) => {
      // The tokens only describe the literal they were built from; an edit or a reused node can
      // replace it rather than extend it, and then the whole prefix has to go.
      if (!text.startsWith(highlighted().text)) {
        setHighlighted({ tokens: [], text: "" });
      }

      if (isServer) {
        tokenize();
        return;
      }

      clearTimeout(settle);
      settle = setTimeout(tokenize, SETTLE_MS);

      if (text.length > MAX_STREAMING_HIGHLIGHT_BYTES) {
        return;
      }
      // Nothing is highlighted yet, so waiting for the frame would paint the block plain first —
      // visible on any block under the cap that mounts already complete. Blocks over the cap
      // still flash: they take the settle timer instead, to keep a long pass off the mount frame.
      if (highlighted().text === "") {
        tokenize();
        return;
      }
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          tokenize();
        });
      }
    })
  );

  onCleanup(() => {
    clearTimeout(settle);
    if (frame) {
      cancelAnimationFrame(frame);
    }
  });

  return (
    <pre class={props.class}>
      <code class={`language-${props.node.info || ""} ${props.codeClass || ""}`}>
        <For each={highlighted().tokens}>{(token) => renderToken(token)}</For>
        {/* The tail is whatever arrived since the last pass: colour may lag a frame, text never does. */}
        {literal().slice(highlighted().text.length)}
      </code>
    </pre>
  );
};
